/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';
import parsePublishResponse from '../../utils/PublishResponseUtils';

/**
 * PublishGraphService - Handles publishing an existing draft graph to live
 * API: PUT /graphs/:graphId/publish
 *
 * Used when:
 * - Publishing a DRAFT graph (changes status from DRAFT to LIVE)
 */
export default class PublishGraphService extends BaseService {
  constructor() {
    super(constants.PUBLISH_GRAPH, 'publishGraphStatus');
  }

  getErrorMessage(data, fallback) {
    return data?.error || data?.message || fallback;
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const {
      graphId,
      graphName,
      version,
      comment,
      isDefault,
      defaultVersionVal,
      graphData,
      targetEnv,
      targetTenantId,
      targetTenantName,
      targetTenantTitle
    } = payload;

    this.targetTenantTitle = targetTenantTitle;

    const requestData = {
      graphName,
      version,
      comment,
      isDefault,
      defaultVersionVal,
      graphData,
      ...(payload.baseRevision != null && { baseRevision: payload.baseRevision }),
      ...(payload.conflictResolutions != null && { conflictResolutions: payload.conflictResolutions }),
      ...(targetEnv && { targetEnv }),
      ...(targetTenantId && { targetTenantId }),
      ...(targetTenantName && { targetTenantName })
    };

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').put(`/graphs/${graphId}/publish`, requestData, {
        headers: enhancedHeaders
      });
    }
    return this.getClient('aiadmin').put(`/graphs/${graphId}/publish`, requestData, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    if (data && data.success) {
      const { message, langMessage, type } = parsePublishResponse(
        data,
        'publishResponse.GRAPH_PUBLISHED_SUCCESSFULLY',
        'publish',
        { targetTenantTitle: this.targetTenantTitle }
      );

      const notification = {
        open: true,
        type,
        message,
        langMessage
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      const graphModel = Framework.getStoreRegistry().getStore('graphModel');
      graphModel.clearError();
      Framework.getEventManager().publish(constants.PUBLISH_GRAPH_SUCCESS, { data });
    } else {
      const msg = this.getErrorMessage(data, 'Unknown error');
      const notification = {
        open: true,
        type: 'error',
        message: `Failed to publish graph: ${msg}`
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('graphModel').setError(msg);
      Framework.getEventManager().publish(constants.PUBLISH_GRAPH_ERROR);
    }
  }

  handleError(error) {
    const { response } = error;
    if (response) {
      const { status, data } = response;
      if (status === 401) {
        utils.redirectToLogin();
      }
      if (status === 409 && data?.contentConflict) {
        Framework.getEventManager().publish(constants.PUBLISH_GRAPH_CONFLICT, { data });
        Framework.getStoreRegistry()
          .getStore('graphModel')
          .setError(this.getErrorMessage(data, 'Graph was modified by another user.'));
        Framework.getEventManager().publish(constants.PUBLISH_GRAPH_ERROR);
        return;
      }
      const msg =
        status === 409
          ? this.getErrorMessage(data, 'Graph was modified by another user. Please refresh and try again.')
          : this.getErrorMessage(data, 'Failed to publish graph. Please try again.');
      const notification = {
        open: true,
        message: msg,
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('graphModel').setError(msg);
    } else if (error.message === 'timeout of 60000ms exceeded') {
      const notification = {
        open: true,
        message: 'Request timeout. Please try again.',
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('graphModel').setError('Request timeout. Please try again.');
    }
    Framework.getEventManager().publish(constants.PUBLISH_GRAPH_ERROR);
  }
}
